import { User } from "oidc-client-ts";
import { LicenseInfo } from "@mui/x-license";
import { config } from "@/config/env";
import { QueryClient } from "react-query";
import authProvider from "@/app/providers/auth";
import { getAccessToken } from "@/features/auth/storage";
import { handleTenancyApiError, requireActiveTenancy } from "@/features/tenancy";
import { notify } from "@/common/notifications";
import {
  type Client,
  createImmiClient,
  createImmiApi,
  createImmiProvider,
  createResourceService,
  type CreateResourceServiceConfig,
  type DataProvider,
  type ImmiClientOptions,
  type Resource,
} from "@imminently/immi-query";

const isB2C = config.AUTH_IS_B2C === "true";

/**
 * OIDC library-owned storage exception. Portal-owned entries must use
 * browserStorage through their feature adapter instead.
 */
export const getUser = (authority: string, clientId: string, storage = sessionStorage) => {
  const oidcStorage = storage.getItem(`oidc.user:${authority}:${clientId}`);
  if (!oidcStorage) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
};

export const getCurrentUser = () => {
  return getUser(config.AUTH_ENDPOINT, config.AUTH_CLIENT_ID);
};

const isTenancyFreeEndpoint = (endpoint?: string): boolean => {
  const normalizedEndpoint = endpoint?.replace(/^\/+/, "").split("?")[0].replace(/\/$/, "");
  return (
    normalizedEndpoint === "auth/me" ||
    normalizedEndpoint === "auth/webPreviewAsset" ||
    normalizedEndpoint?.startsWith("auth/webPreviewAsset/") === true
  );
};

export const azureAuthBearerHeader = async (endpoint?: string) => {
  let accessToken = "";
  try {
    accessToken = getAccessToken() || "";
    if (accessToken && typeof authProvider.instance?.tokenExpired === "function" && typeof authProvider === "function") {
      if (authProvider.instance.tokenExpired(accessToken)) {
        await authProvider("silentAuthCheck");
        accessToken = getAccessToken() || "";
      }
    }
  } catch (error: any) {
    console.error("Failed to do silent auth check", error);
  }

  const user = getUser(config.AUTH_ENDPOINT, config.AUTH_CLIENT_ID);

  if (accessToken || user) {
    const headers = {
      Authorization: `Bearer ${accessToken || user?.id_token || ""}`,
    } as Record<string, string>;
    if (!isTenancyFreeEndpoint(endpoint)) {
      headers["X-TENANCY"] = requireActiveTenancy();
    }
    return headers;
  }

  return {};
};

const withTenancyErrorHandling = (client: Client): Client => {
  const observe = async <T,>(request: Promise<T>): Promise<T> => {
    try {
      return await request;
    } catch (error) {
      handleTenancyApiError(error);
      throw error;
    }
  };

  const wrappedClient = ((endpoint: string, options?: Parameters<Client>[1]) =>
    observe(client(endpoint, options))) as Client;
  wrappedClient.get = (endpoint, options) => observe(client.get(endpoint, options));
  wrappedClient.post = (endpoint, body, options) => observe(client.post(endpoint, body, options));
  wrappedClient.patch = (endpoint, body, options) => observe(client.patch(endpoint, body, options));
  wrappedClient.delete = (endpoint, options) => observe(client.delete(endpoint, options));
  wrappedClient.baseUrl = client.baseUrl;
  wrappedClient.getAuthHeaders = client.getAuthHeaders;
  wrappedClient.enforceTrailingSlash = client.enforceTrailingSlash;
  return wrappedClient;
};

export const global = (function () {
  const options: ImmiClientOptions = {
    host: config.BACKEND_URL ?? "",
    path: "",
    getAuthHeaders: azureAuthBearerHeader,
    enforceTrailingSlash: config.ENFORCE_URL_RESOURCE_SLASH === "true",
  };

  let muiLicense = config.MUIX;

  if (muiLicense) {
    LicenseInfo.setLicenseKey(muiLicense);
  } else {
    console.error("No muiLicense found in config, using unregistered version.");
  }

  const client: Client = withTenancyErrorHandling(createImmiClient(options));
  const dataProvider: DataProvider = createImmiProvider(client);
  const queryClient = new QueryClient();

  return {
    client,
    dataProvider,
    queryClient,
    config,
  };
})();

// @ts-ignore
window.client = global.client;

type ResourceConfig = Omit<CreateResourceServiceConfig, "resource" | "client" | "dataProvider" | "queryClient">;

/**
 * A utility function to create a resource service with the global client, dataProvider, and queryClient.
 * @param resource The resource name
 * @param config Optional additional config parameters
 */
export const createAppResourceService = <T extends Resource>(resource: string, config: ResourceConfig = {}) =>
  createResourceService<T>({
    resource,
    client: global.client,
    dataProvider: global.dataProvider,
    queryClient: global.queryClient,
    defaultQueryOptions: {
      onError: (error: any) => {
        if (handleTenancyApiError(error)) {
          return;
        }
        if (error.message) {
          notify.error(error.message, { autoClose: 5000 });
        } else {
          notify.error("An error has occurred in the backend. Contact Support", { autoClose: 5000 });
        }
      },
    },
    ...config,
  });



// @ts-ignore
window.API = createImmiApi({
  client: global.client,
  name: "Test API"
});
